import React, { FC, ReactNode, useCallback } from 'react';
import classnames from 'classnames';
import { RouterLocation } from '@funtech-int/rival';
import { useAnalytics, useFeatureFlags, useAuthManager } from '@funtech-int/yango-shared';

import { personalRoute } from '@/scenarios/personal/routes';
import { channelsRoute } from '@/scenarios/channels/routes';
import { mainRoute } from '@/scenarios/home/routes';
import { useHasSubscription } from '@/shared/hooks/useHasSubscription';
import { useHeaderItems } from '@/shared/hooks/useHeaderItems';
import { useI18n } from '@/app/hooks/useI18n';
import { RouterLink } from '@/shared/components/RouterLink/RouterLink';
import { createAnalyticsEvents } from '@/app/lib/createAnalyticsEvents';
import { PageId } from '@/shared/types/pathId';
import { useProfileManager } from '@/shared/hooks/useProfileManager';
import { sportRoute } from '@/scenarios/sport';

import { HeaderHint } from '../HeaderHint/HeaderHint';

import styles from './_Navigation.module.css';

interface TabConfig {
    id: PageId;
    title: string;
    renderHint?: () => ReactNode;
    to: RouterLocation<any, any>;
}

export interface TabsProps {
    searchVisible: boolean;
    isHeaderVisible: boolean;
}
interface NavLinkProps {
    tab: TabConfig;
}

const NavLink: FC<NavLinkProps> = ({ tab }) => {
    const { title, renderHint, id, to } = tab;

    const hasSubscription = useHasSubscription();
    const analytics = useAnalytics(createAnalyticsEvents);

    const handleOnClick = useCallback(
        () => analytics.headerNavigated(id, hasSubscription),
        [analytics, id, hasSubscription]
    );

    return (
        <li className={classnames(styles.rootLink, styles.tab)}>
            <RouterLink
                to={to}
                onClick={handleOnClick}
                className={styles.link}
                activeClassName={styles.link_active}
            >
                {title}
            </RouterLink>
            {renderHint?.()}
        </li>
    );
};

export const Tabs: FC<TabsProps> = ({ searchVisible, isHeaderVisible }) => {
    const { isAuthorized } = useAuthManager();
    const { isNewlyRegisteredUser } = useProfileManager();

    const i18n = useI18n();

    const activeMenuItemTitles = useHeaderItems();
    const sportEnabled = useFeatureFlags().flags.get('sportEnabled', 'boolean', false);
    const isHintVisible = !searchVisible && isHeaderVisible;

    const tabs: TabConfig[] = [
        {
            id: PageId.MAIN,
            title: i18n('menu__home_title'),
            to: mainRoute(),
        },
    ];

    if (sportEnabled) {
        tabs.push({
            id: 'sport' as PageId,
            title: i18n('menu__sport_title'),
            to: sportRoute(),
        });
    }

    if (activeMenuItemTitles.includes(PageId.CHANNELS)) {
        tabs.push({
            id: PageId.CHANNELS,
            title: i18n('menu__channels_title'),
            to: channelsRoute(),
        });
    }

    if (isAuthorized) {
        tabs.push({
            id: PageId.PERSONAL,
            renderHint: !isNewlyRegisteredUser
                ? () => (
                      <HeaderHint
                          id="personal-hint"
                          text={i18n('allplay__nav_my_tooltip')}
                          wrapperClassname={classnames(
                              styles.personalWrapper,
                              styles['hint-wrapper']
                          )}
                          getIsHintShowedInitial={(router) => mainRoute.is(router.location)}
                          hintClassname={classnames(styles.personalHint, styles.hint)}
                          isHintVisible={isHintVisible}
                      />
                  )
                : undefined,
            title: i18n('menu__my_title'),
            to: personalRoute(),
        });
    }

    return (
        <ul className={styles.tabs}>
            {tabs.map((tab) => (
                <NavLink key={tab.id} tab={tab} />
            ))}
        </ul>
    );
};
